import React, { useRef } from 'react';
import AsyncSelect from 'react-select/async';
import AsyncDebounce from '../utils/AsyncDebounce.js';
import { searchAddresses } from '../api/index.js';
import { type AddressResult } from '../api/types.js';

export interface AddressLocation {
  address: string;
  lat: number;
  lng: number;
}

interface Props {
  label: string;
  placeholder: string;
  value: string;
  onChange: (loc: AddressLocation) => void;
}

const SELECT_STYLES = {
  control: (base: any, state: any) => ({
    ...base,
    background: 'var(--bg2)',
    border: `1px solid ${state.isFocused ? 'var(--gold)' : 'var(--bm)'}`,
    borderRadius: 4,
    boxShadow: 'none',
    minHeight: 28,
    cursor: 'text',
    '&:hover': { borderColor: 'var(--gold)' },
  }),
  menu: (base: any) => ({
    ...base,
    background: '#13161e',
    border: '1px solid #2a2f3d',
    borderRadius: 4,
    boxShadow: '0 4px 16px rgba(0,0,0,.6)',
    zIndex: 9999,
  }),
  menuList: (base: any) => ({
    ...base,
    background: '#13161e',
    borderRadius: 4,
    padding: 0,
  }),
  option: (base: any, state: any) => ({
    ...base,
    background: state.isFocused ? 'rgba(212,175,55,.12)' : 'transparent',
    color: state.isFocused ? 'var(--gold)' : 'var(--t2)',
    fontFamily: 'var(--mono)',
    fontSize: 11,
    padding: '6px 10px',
    cursor: 'pointer',
  }),
  input: (base: any) => ({
    ...base,
    color: 'var(--t1)',
    fontFamily: 'var(--mono)',
    fontSize: 11,
    margin: 0,
    padding: 0,
  }),
  placeholder: (base: any) => ({
    ...base,
    color: 'var(--t3)',
    fontFamily: 'var(--mono)',
    fontSize: 11,
  }),
  singleValue: (base: any) => ({
    ...base,
    color: 'var(--t1)',
    fontFamily: 'var(--mono)',
    fontSize: 11,
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: '2px 8px',
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: 'var(--t3)',
    padding: '0 6px',
    '&:hover': { color: 'var(--gold)' },
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  noOptionsMessage: (base: any) => ({
    ...base,
    color: 'var(--t3)',
    fontFamily: 'var(--mono)',
    fontSize: 11,
  }),
  loadingMessage: (base: any) => ({
    ...base,
    color: 'var(--t3)',
    fontFamily: 'var(--mono)',
    fontSize: 11,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function AddressField({ label, placeholder, value, onChange }: Props) {
  const loadOptions = useRef(
    AsyncDebounce(async (input: string) => {
      if (!input || input.length < 3) return [];
      try {
        return await searchAddresses(input);
      } catch {
        return [];
      }
    }, 350)
  ).current;

  const handleChange = (option: AddressResult | null) => {
    if (!option) return;
    onChange({
      address: option.formattedAddress,
      lat: option.location.lat,
      lng: option.location.lng,
    });
  };

  return (
    <div className="fg">
      <label className="fl">{label}</label>
      <AsyncSelect<AddressResult>
        placeholder={placeholder}
        value={value ? ({ formattedAddress: value } as AddressResult) : null}
        // @ts-ignore
        loadOptions={loadOptions}
        getOptionLabel={(o) => o.formattedAddress}
        getOptionValue={(o) => o.formattedAddress}
        onChange={handleChange}
        defaultOptions={[]}
        noOptionsMessage={({ inputValue }) => (inputValue.length < 3 ? 'Type at least 3 characters…' : 'No results')}
        loadingMessage={() => 'Searching…'}
        styles={SELECT_STYLES}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
      />
    </div>
  );
}

export default AddressField;
